import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Card, compareMoney, Money } from '@elysian/ui';
import type { ContactTransaction } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { iso, shortDate } from '../invoices/shared';
import { ExportMenu } from '../shared/ExportMenu';
import type { ExportColumn } from '../shared/exportList';

type Period = '3m' | '12m' | 'all' | 'custom';
const PERIODS: { key: Period; label: string }[] = [
  { key: '3m', label: 'Last 3 months' }, { key: '12m', label: 'Last 12 months' }, { key: 'all', label: 'Everything' }, { key: 'custom', label: 'Choose dates' },
];

const KIND: Record<string, string> = {
  sales_invoice: 'Invoice', sales_credit: 'Credit note', purchase_bill: 'Bill', purchase_credit: 'Credit note',
  receipt: 'Payment received', payment: 'Payment made', opening_balance: 'Opening balance', manual: 'Journal',
  reversal: 'Reversal', bank_transfer: 'Bank', fx_revaluation: 'Currency revaluation', fx_realised: 'Exchange difference', year_end: 'Year end',
};
const kindOf = (t: ContactTransaction) => KIND[t.source] ?? 'Journal';

function monthsBack(today: string, months: number): string {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - months);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const docLink = (d: NonNullable<ContactTransaction['document']>) =>
  d.type === 'sales_invoice' ? `/invoices/${d.id}` : d.type === 'sales_credit' ? `/credit-notes/${d.id}`
    : d.type === 'receipt' || d.type === 'payment' ? `/receipts/${d.id}` : `/bills/${d.id}`;

/**
 * A customer's or supplier's transactions (#11): invoices, credit notes, bills
 * and payments with a running balance, for a period (the last 12 months to
 * start with), the balance now, and an export of what's shown.
 */
export function ContactTransactions({ contactId, role }: { contactId: string; role: 'customer' | 'supplier' }) {
  const { source, today } = useSession();
  const now = iso(today());
  const [period, setPeriod] = useState<Period>('12m');
  const [custom, setCustom] = useState({ from: monthsBack(now, 1), to: now });
  const range = period === 'custom' ? custom
    : period === 'all' ? { from: undefined, to: now }
    : { from: monthsBack(now, period === '3m' ? 3 : 12), to: now };
  const bad = period === 'custom' && custom.from && custom.to && custom.from > custom.to;
  const data = useAsync(
    () => (bad ? Promise.reject(new Error('The start is after the end')) : source.contactTransactions(contactId, { ...range, side: role })),
    [source, contactId, role, range.from, range.to], { keep: true });
  const t = data.state === 'ready' ? data.data : null;
  const owes = role === 'customer' ? 'Owes you' : 'You owe';

  const columns: ExportColumn<ContactTransaction>[] = [
    { header: 'Date', kind: 'date', value: (r) => r.date },
    { header: 'Type', value: kindOf },
    { header: 'Number', value: (r) => r.document?.number ?? r.journal_no },
    { header: 'Reference', value: (r) => r.document?.reference ?? '' },
    { header: 'Description', value: (r) => r.description ?? '' },
    { header: 'Due', kind: 'date', value: (r) => r.document?.due_date ?? '' },
    { header: 'Debit', kind: 'money', value: (r) => r.debit },
    { header: 'Credit', kind: 'money', value: (r) => r.credit },
    { header: 'Balance', kind: 'money', value: (r) => r.balance },
  ];

  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="c-txns">
      <div className="el-card-head el-txn-head">
        <div>
          <h2 id="c-txns" className="el-h2">Transactions</h2>
          {t && (
            <div className="el-hint">
              {owes} <strong><Money value={t.current_balance} /></strong> now
              {compareMoney(t.overdue, '0') > 0 && <>, <Money value={t.overdue} tone="warn" /> of it overdue</>}
            </div>
          )}
        </div>
        <div className="el-txn-controls">
          <label className="el-date-filter-field">
            <span className="el-vh">Period</span>
            <select className="el-input" aria-label="Period" value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
              {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          {period === 'custom' && (
            <>
              <label className="el-date-filter-field"><span>From</span>
                <input type="date" className="el-input" aria-label="Transactions from" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} /></label>
              <label className="el-date-filter-field"><span>To</span>
                <input type="date" className="el-input" aria-label="Transactions to" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} /></label>
            </>
          )}
          <ExportMenu name={`${t?.name ?? 'Contact'} transactions`} columns={columns} rows={t?.lines ?? []} disabled={!t} />
        </div>
      </div>
      {data.state === 'error' && <div style={{ padding: '0 16px' }}><Banner>{data.error.message}</Banner></div>}
      <div className="el-table-wrap">
        <table className="el-table" aria-label="Transactions">
          <thead>
            <tr>
              <th scope="col">Date</th><th scope="col">Type</th><th scope="col">Number</th><th scope="col">Details</th>
              <th scope="col" className="el-num">Debit</th><th scope="col" className="el-num">Credit</th><th scope="col" className="el-num">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.state === 'loading' && <tr><td colSpan={7}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading transactions" /></td></tr>}
            {t && t.from && (
              <tr className="el-txn-carried">
                <td>{shortDate(t.from)}</td><td colSpan={5}>Brought forward</td><td className="el-num"><Money value={t.opening_balance} /></td>
              </tr>
            )}
            {t?.lines.map((r) => (
              <tr key={r.journal_id}>
                <td style={{ whiteSpace: 'nowrap' }}>{shortDate(r.date)}</td>
                <td>{kindOf(r)}</td>
                <td>{r.document ? <Link className="el-row-link" to={docLink(r.document)}>{r.document.number ?? 'Draft'}</Link> : <span className="el-muted">{r.journal_no}</span>}</td>
                <td>
                  {r.document ? (r.document.reference ? `Ref ${r.document.reference}` : '') : (r.description ?? '')}
                  {r.document?.outstanding && compareMoney(r.document.outstanding, '0') > 0 && (
                    <div className="el-hint"><Money value={r.document.outstanding} /> still to pay{r.document.due_date ? `, due ${shortDate(r.document.due_date)}` : ''}</div>
                  )}
                </td>
                <td className="el-num">{compareMoney(r.debit, '0') > 0 ? <Money value={r.debit} /> : ''}</td>
                <td className="el-num">{compareMoney(r.credit, '0') > 0 ? <Money value={r.credit} /> : ''}</td>
                <td className="el-num"><Money value={r.balance} /></td>
              </tr>
            ))}
            {t && t.lines.length === 0 && <tr><td colSpan={7} className="el-muted">Nothing in these dates.</td></tr>}
          </tbody>
          {t && t.lines.length > 0 && (
            <tfoot>
              <tr><td colSpan={6}><strong>Balance at {shortDate(t.to)}</strong></td><td className="el-num"><strong><Money value={t.closing_balance} /></strong></td></tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="el-hint" style={{ margin: '8px 16px 4px' }}>In euro, the books’ currency. {role === 'customer' ? 'Debits are what they’re charged; credits what they’ve paid or been credited.' : 'Credits are their bills; debits what you’ve paid or been credited.'}</p>
    </Card>
  );
}
